import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BookOpen, ArrowRight, Lock, Clock, ShoppingBag } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyCoursesPage() {
  const { user, loading: authLoading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    axios.get(`${API}/api/my-courses`, { withCredentials: true })
      .then((res) => setCourses(res.data.courses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 text-center text-white/50" data-testid="my-courses-loading">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 text-center" data-testid="my-courses-signin">
        <Lock size={36} className="mx-auto text-amber-400 mb-4" />
        <h1 className="text-3xl font-bold text-white">Sign in to access your courses</h1>
        <p className="mt-3 text-sm text-white/50">Use the email you purchased with.</p>
        <Link to="/login" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-amber-400 text-black font-bold text-sm hover:bg-amber-300 transition-colors" data-testid="signin-cta">
          Sign in <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="my-courses-page">
      <section className="px-4 sm:px-6 lg:px-8 pb-10 relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-amber-400/[0.04] blur-[140px] pointer-events-none" />
        <div className="relative mx-auto max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400/80 mb-2">Member Area</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            My <span className="text-amber-400">Courses</span>
          </h1>
          <p className="mt-3 text-sm text-white/50">Welcome back, {user.name || user.email.split('@')[0]}.</p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8" data-testid="courses-list">
        <div className="mx-auto max-w-5xl">
          {courses.length === 0 ? (
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center" data-testid="no-courses">
              <ShoppingBag size={32} className="mx-auto text-white/30 mb-4" />
              <h2 className="text-xl font-bold text-white">No courses yet</h2>
              <p className="mt-2 text-sm text-white/50">Browse the catalog to start your trading journey.</p>
              <Link to="/all-courses" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-black font-bold text-sm hover:bg-amber-300 transition-colors">
                Explore Courses <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {courses.map((c) => (
                <Link
                  key={c.slug}
                  to={`/my-courses/${c.slug}`}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-amber-400/30 hover:bg-amber-400/[0.03] transition-all"
                  data-testid={`course-card-${c.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-11 h-11 rounded-xl bg-amber-400/10 flex items-center justify-center">
                      <BookOpen size={18} className="text-amber-400" />
                    </div>
                    {!c.has_content && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-400/10 text-sky-400 text-[10px] font-bold uppercase tracking-wider">
                        <Clock size={11} /> Soon
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-white">{c.name}</h3>
                  {c.subtitle && <p className="text-xs text-white/50 mt-1">{c.subtitle}</p>}
                  <div className="mt-4 pt-4 border-t border-white/[0.05] flex items-center justify-between text-xs">
                    <span className="text-white/40">{c.has_content ? `${c.item_count} items` : 'Materials being prepared'}</span>
                    <span className="text-amber-400 group-hover:translate-x-0.5 transition-transform">Open →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
